import { useState } from 'react'
import { Download, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { toast } from 'sonner'
import * as XLSX from 'xlsx'
import autoTable from 'jspdf-autotable'
import { createThaiPDF, thaiTableStyles, thaiHeadStyles } from '@/src/lib/pdf/thai-font'
import type { ReportDateRange } from '@/src/types/admin/reports'

export interface ReportColumn {
  key: string
  label: string
  format?: (v: unknown) => string
}

export interface SummaryStat {
  label: string
  value: string | number
  description?: string
}

interface ReportPageLayoutProps {
  title: string
  description: string
  audience: string
  icon: React.ElementType
  columns: ReportColumn[]
  fetchFn: (range: ReportDateRange) => Promise<{ data: unknown }>
  extractRows: (data: unknown) => Record<string, unknown>[]
  extractSummary?: (data: unknown) => SummaryStat[]
  filenamePrefix: string
}

function defaultRange(): ReportDateRange {
  const now = new Date()
  // First day of the current month
  const from = new Date(now.getFullYear(), now.getMonth(), 1)
  // Last day of the current month (day 0 of next month)
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  return {
    date_from: toLocalISODate(from),
    date_to: toLocalISODate(to),
  }
}

// Format a Date to YYYY-MM-DD using local date parts (avoids UTC off-by-one shifts)
function toLocalISODate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function cellDisplay(col: ReportColumn, v: unknown): string {
  if (col.format) return col.format(v)
  if (v == null) return '-'
  if (typeof v === 'number') return v.toLocaleString('th-TH')
  return String(v)
}

export function ReportPageLayout({
  title,
  description,
  audience,
  icon: Icon,
  columns,
  fetchFn,
  extractRows,
  extractSummary,
  filenamePrefix,
}: ReportPageLayoutProps) {
  const [range, setRange] = useState<ReportDateRange>(defaultRange)
  const [loading, setLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null)
  const [summary, setSummary] = useState<SummaryStat[] | null>(null)

  const loadData = async () => {
    setLoading(true)
    try {
      const result = await fetchFn(range)
      const extracted = extractRows(result.data)
      setRows(extracted)
      setSummary(extractSummary ? extractSummary(result.data) : null)
      if (extracted.length === 0) {
        toast.info('ไม่มีข้อมูลในช่วงวันที่ที่เลือก')
      }
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูลได้')
    } finally {
      setLoading(false)
    }
  }

  const handleExcelExport = () => {
    if (!rows || rows.length === 0) return
    const data = rows.map(row => {
      const obj: Record<string, unknown> = {}
      columns.forEach(col => { obj[col.label] = cellDisplay(col, row[col.key]) })
      return obj
    })
    const ws = XLSX.utils.json_to_sheet(data)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Report')
    XLSX.writeFile(wb, `${filenamePrefix}_${range.date_from}_${range.date_to}.xlsx`)
    toast.success('ดาวน์โหลด Excel สำเร็จ')
  }

  const handlePdfExport = async () => {
    if (!rows || rows.length === 0) return
    setPdfLoading(true)
    try {
      const doc = await createThaiPDF()
      doc.setFontSize(14)
      doc.text(title, 14, 16)
      doc.setFontSize(8)
      doc.text(`${range.date_from} ถึง ${range.date_to}`, 14, 22)
      autoTable(doc, {
        startY: 28,
        columns: columns.map(col => ({ header: col.label, dataKey: col.key })),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        body: rows.map(row => {
          const obj: Record<string, string> = {}
          columns.forEach(col => { obj[col.key] = cellDisplay(col, row[col.key]) })
          return obj
        }) as any,
        styles: { ...thaiTableStyles, fontSize: 7 },
        headStyles: { ...thaiHeadStyles, fillColor: [37, 99, 235] },
      })
      doc.save(`${filenamePrefix}_${range.date_from}_${range.date_to}.pdf`)
      toast.success('ดาวน์โหลด PDF สำเร็จ')
    } catch {
      toast.error('ไม่สามารถสร้าง PDF ได้')
    } finally {
      setPdfLoading(false)
    }
  }

  const summaryColCount = summary ? Math.min(summary.length, 4) : 2

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-50 rounded-lg shrink-0">
            <Icon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
            <p className="text-sm text-gray-500">{description}</p>
          </div>
        </div>
        <Badge variant="secondary" className="shrink-0">{audience}</Badge>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs mb-1 block">วันที่เริ่ม</Label>
              <Input
                type="date"
                value={range.date_from}
                onChange={(e) => setRange(r => ({ ...r, date_from: e.target.value }))}
                className="h-9 text-sm w-40"
              />
            </div>
            <div>
              <Label className="text-xs mb-1 block">วันที่สิ้นสุด</Label>
              <Input
                type="date"
                value={range.date_to}
                onChange={(e) => setRange(r => ({ ...r, date_to: e.target.value }))}
                className="h-9 text-sm w-40"
              />
            </div>
            <Button onClick={loadData} disabled={loading} className="h-9">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              โหลดข้อมูล
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {summary && summary.length > 0 && (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: `repeat(${summaryColCount}, minmax(0, 1fr))` }}
        >
          {summary.map((stat, i) => (
            <Card key={i}>
              <CardHeader className="pb-1 pt-4 px-4">
                <CardDescription className="text-xs">{stat.label}</CardDescription>
                <CardTitle className="text-lg">{stat.value}</CardTitle>
              </CardHeader>
              {stat.description && (
                <CardContent className="pb-3 pt-0 px-4">
                  <p className="text-xs text-gray-500">{stat.description}</p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Data Preview Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium">ตัวอย่างข้อมูล</CardTitle>
            {rows && rows.length > 0 && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleExcelExport}>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Excel
                </Button>
                <Button variant="outline" size="sm" onClick={handlePdfExport} disabled={pdfLoading}>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  {pdfLoading ? 'กำลังสร้าง...' : 'PDF'}
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {rows === null ? (
            <div className="py-14 text-center text-sm text-gray-400">
              กดปุ่ม "โหลดข้อมูล" เพื่อแสดงตัวอย่างรายงาน
            </div>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center text-sm text-gray-400">
              ไม่มีข้อมูลในช่วงวันที่ที่เลือก
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className="text-left py-2 px-3 text-xs font-semibold text-gray-600 bg-gray-50 border-b whitespace-nowrap"
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b hover:bg-gray-50 transition-colors">
                      {columns.map(col => (
                        <td key={col.key} className="py-2 px-3 text-xs text-gray-700 whitespace-nowrap">
                          {cellDisplay(col, row[col.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
